import { mockLocations } from '@/lib/mock-data';
import { getAllPinnedLocationNumbers } from '@/constants/customer-ops';
import type { Location, LocationUpdate } from '@/types/location';

const PINNED_LOCATION_NUMBERS = new Set(getAllPinnedLocationNumbers());

const DEV_SEED_LOCATIONS = mockLocations.filter((location) =>
  PINNED_LOCATION_NUMBERS.has(location.location_number)
);
const devLocationOverrides = new Map<string, Partial<Location>>();

function trimNullable(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeDevLocationUpdate(data: LocationUpdate): Partial<Location> {
  const update: Partial<Location> = { ...data };

  if (data.store_name !== undefined) update.store_name = trimNullable(data.store_name);
  if (data.campaign_start !== undefined) update.campaign_start = data.campaign_start || null;
  if (data.campaign_end !== undefined) update.campaign_end = data.campaign_end || null;
  if (data.baseline !== undefined) update.baseline = trimNullable(data.baseline);
  if (data.case_study_url !== undefined) update.case_study_url = trimNullable(data.case_study_url);
  if (data.campaign_status !== undefined) update.campaign_status = data.campaign_status;

  return update;
}

function applyDevOverrides(location: Location): Location {
  const override = devLocationOverrides.get(location.id);
  return override ? { ...location, ...override } : location;
}

function mergeCampaignFields(location: Location, seed: Location): Location {
  return applyDevOverrides({
    ...location,
    store_name: location.store_name ?? seed.store_name ?? null,
    campaign_start: location.campaign_start ?? seed.campaign_start ?? null,
    campaign_end: location.campaign_end ?? seed.campaign_end ?? null,
    baseline: location.baseline ?? seed.baseline ?? null,
    case_study_url: location.case_study_url ?? seed.case_study_url ?? null,
    campaign_status: location.campaign_status ?? seed.campaign_status ?? null,
  });
}

export function applyDevCustomerOpsLocationOverlay(location: Location): Location {
  const seed = DEV_SEED_LOCATIONS.find((entry) => entry.location_number === location.location_number);
  return seed ? mergeCampaignFields(location, seed) : location;
}

export function mergeDevCustomerOpsLocations(locations: Location[]): Location[] {
  const merged = locations.map(applyDevCustomerOpsLocationOverlay);

  for (const seed of DEV_SEED_LOCATIONS) {
    if (!merged.some((location) => location.location_number === seed.location_number)) {
      merged.unshift(applyDevOverrides(seed));
    }
  }

  return merged;
}

export function findDevCustomerOpsLocationById(id: string): Location | null {
  const location = DEV_SEED_LOCATIONS.find((entry) => entry.id === id);
  return location ? applyDevOverrides(location) : null;
}

export function updateDevCustomerOpsLocation(id: string, data: LocationUpdate): Location | null {
  const current = findDevCustomerOpsLocationById(id);
  if (!current) return null;

  const update = normalizeDevLocationUpdate(data);
  const updatedAt = new Date().toISOString();
  const nextOverride = {
    ...devLocationOverrides.get(id),
    ...update,
    updated_at: updatedAt,
  };
  const updatedLocation: Location = {
    ...current,
    ...nextOverride,
  };

  devLocationOverrides.set(id, nextOverride);
  return updatedLocation;
}
